import { reviewDraftSchema, type ReviewDecision, type ReviewDraft } from '@console/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../api.js'
import { fetchCsrfToken } from '../auth/api.js'

/**
 * The draft under review.
 *
 * **`staleTime: 0` and no background refetch**, deliberately. Every other
 * surface in this console benefits from a warm cache; this one must not have
 * one. An operator is deciding against a specific version, and a draft that
 * refreshed underneath them would move the fingerprint their answers are bound
 * to — which is precisely the conflict the 409 exists to surface rather than
 * to cause.
 */
export function useReviewDraft(runId: string) {
  return useQuery({
    queryKey: ['review', runId],
    queryFn: () => apiGet(`/runs/${encodeURIComponent(runId)}/review`, reviewDraftSchema),
    staleTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface SubmitDecisionsInput {
  draftFingerprint: string
  acceptAllKnown: boolean
  decisions: ReviewDecision[]
}

/**
 * Submit per-field decisions.
 *
 * **No operator field** — amendment 4. The server resolves identity from the
 * session, and there is nothing here for a browser to claim with.
 *
 * The response replaces the cached draft directly rather than invalidating it:
 * the server has just told us the new version, and a refetch would open a
 * window in which the screen holds a fingerprint it has not painted.
 */
export function useSubmitDecisions(runId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SubmitDecisionsInput): Promise<ReviewDraft> => {
      const csrfToken = await fetchCsrfToken()
      return apiPost(
        `/runs/${encodeURIComponent(runId)}/review/decisions`,
        input,
        csrfToken,
        reviewDraftSchema,
      )
    },
    onSuccess: (draft) => {
      queryClient.setQueryData(['review', runId], draft)
    },
  })
}
